import { useEffect, useMemo, useState } from "react";
import { Button, Empty, Select, Spin } from "antd";
import { getUserDetail, searchUsers } from "../../api/user";
import UserRegister from "./UserRegister/UserRegister";
const MIN_QUERY_LENGTH = 2;

function getDisplayName(user) {
  return user.name || user.englishName || user.username || "未命名用户";
}

function toOption(user) {
  return {
    value: user._id,
    label: getDisplayName(user),
    user,
  };
}

function UserOption({ user }) {
  return (
    <div style={{ padding: "2px 0" }}>
      <div>
        <strong>{user.name || "未命名用户"}</strong>
        {user.englishName && (
          <span style={{ color: "#8c8c8c", marginLeft: 8 }}>
            {user.englishName}
          </span>
        )}
      </div>
      {user.matches?.length > 0 && (
        <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 2 }}>
          {user.matches.slice(0, 2).map((match, index) => (
            <span key={`${match.kind}-${match.type}-${index}`}>
              {index > 0 ? " · " : ""}
              {match.type} {match.maskedValue}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UserSelect({
  value,
  onChange,
  placeholder = "搜索姓名、证件号码或联系方式",
  allowCreate = true,
  disabled = false,
}) {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const normalizedQuery = query.trim();
    let ignore = false;

    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setUsers([]);
      setHasMore(false);
      setSearchError(false);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setSearchError(false);

    const timer = setTimeout(async () => {
      try {
        const { data } = await searchUsers(normalizedQuery);
        if (ignore) return;
        setUsers(Array.isArray(data.items) ? data.items : []);
        setHasMore(Boolean(data.hasMore));
      } catch {
        if (ignore) return;
        setUsers([]);
        setHasMore(false);
        setSearchError(true);
      } finally {
        if (!ignore) setLoading(false);
      }
    }, 300);

    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [query]);

  // 有值、但选项里没有对应用户时（比如父组件直接传入 id），按 id 取详情，补一个选项
  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
      return undefined;
    }
    if (
      selectedOption?.value === value ||
      users.some((user) => user._id === value)
    ) {
      return undefined;
    }

    let ignore = false;
    getUserDetail(value)
      .then(({ data }) => {
        const user = data?._id ? data : data?.data; // 兼容接口直接返回用户，或包了一层 data
        if (!ignore && user?._id) setSelectedOption(toOption(user));
      })
      .catch(() => {});

    return () => {
      ignore = true;
    };
  }, [value, selectedOption, users]);

  const options = useMemo(
    () =>
      users.map((user) => ({
        ...toOption(user),
      })),
    [users],
  );

  const renderedOptions = useMemo(() => {
    // 已选用户不在当前搜索结果里时，补进选项，Select 才能显示名字
    const exists =
      selectedOption &&
      options.some((option) => option.value === selectedOption.value);
    if (!selectedOption || selectedOption.value !== value || exists)
      return options;
    return [selectedOption, ...options];
  }, [options, selectedOption, value]);

  const handleChange = (nextValue, option) => {
    if (option?.user) {
      setSelectedOption(toOption(option.user));
    } else if (!nextValue) {
      setSelectedOption(null);
    }
    setQuery("");
    onChange?.(nextValue, option);
  };

  const handleUserCreated = (user) => {
    const option = toOption(user);
    setUsers((current) => [
      user,
      ...current.filter((item) => item._id !== user._id),
    ]);
    setSelectedOption(option);
    setRegisterOpen(false);
    setQuery("");
    onChange?.(user._id, option);
  };

  const getNotFoundContent = () => {
    if (loading) return <Spin size="small" />;
    if (searchError)
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="搜索失败，请重试"
        />
      );
    if (query.trim().length < MIN_QUERY_LENGTH) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="请输入至少 2 个字符"
        />
      );
    }
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="没有匹配的用户"
      />
    );
  };

  return (
    <>
      <Select
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        allowClear
        disabled={disabled}
        showSearch
        filterOption={false}
        value={value || undefined}
        searchValue={query}
        placeholder={placeholder}
        options={renderedOptions}
        optionLabelProp="label"
        optionRender={(option) => <UserOption user={option.data.user} />}
        notFoundContent={getNotFoundContent()}
        onSearch={setQuery}
        onChange={handleChange}
        onClear={() => setQuery("")}
        popupRender={(menu) => (
          <>
            {menu}
            {hasMore && (
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  color: "#8c8c8c",
                  fontSize: 12,
                  padding: "8px 12px",
                }}
              >
                匹配结果超过 5 条，请继续输入以缩小筛选范围
              </div>
            )}
            {allowCreate && (
              <div
                style={{ borderTop: "1px solid #f0f0f0", padding: 8 }}
                onMouseDown={(event) => event.preventDefault()}
              >
                <Button
                  type="link"
                  block
                  onClick={() => {
                    setDropdownOpen(false); // 先收起下拉，再打开注册抽屉
                    setRegisterOpen(true);
                  }}
                >
                  添加新用户
                </Button>
              </div>
            )}
          </>
        )}
      />
      {allowCreate && (
        <UserRegister
          open={registerOpen}
          onClose={() => setRegisterOpen(false)}
          onSuccess={handleUserCreated}
        />
      )}
    </>
  );
}
